import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, FastField, Field } from "formik";
import * as Yup from "yup";
import HorizontalSeparator from "../common/HorizontalSeparator";
import { Box, Tabs, Tab } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { createSingleTrackStory } from "../../api/singleTrack";
import { getProjects, getProject } from "../../api";
import Button from "../ui/Button";
import { FileInput } from "../common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../images/ImageList";
import { TextField, NumberField } from "../common/FormFields";
import FormGroup from "../common/FormGroup";
import ImageSpecsBadges from "../common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { Tooltip } from "@mui/material";
import AudioPlayerRewind from "../common/AudioPlayer";
import Dropdown from "../common/Dropdown";
import RadioButton from "../common/RadioButton";
import { CompanySingleTrackFolder } from "../../types/requestsOutputs";
import { useNotification } from "../../context/NotificationContext";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import studio from "./StudioModal.module.css";
import stepStyles from "../../pages/track/create/createStep.module.css";
import infoStyles from "./ModalInfoFields.module.css";
import audioForm from "../common/AudioPlayerForm.module.css";

const audioAccept = {
    "audio/mpeg": [".mp3"],
    "audio/wav": [".wav"],
};

interface LocalizedContent {
    name: string;
    description?: string;
    audioUrl: string;
    useProject: boolean;
    selectedProject: string;
    selectedAudioOption: string;
}

interface UnifiedFormValues {
    localizedContent: {
        [key: string]: LocalizedContent;
    };
    address?: string;
    coordinates?: string;
    phone?: string;
    linkUrl?: string;
    downloadLink?: string;
    pictures: string[];
    type: "story" | "event";
    position?: number;
}

interface UnifiedCreateSingleTrackModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    hubId?: string | null;
    folderId?: string | null;
    categoryId?: string | null;
    hub?: CompanySingleTrackFolder | null;
}

// ─── Inner form component ─────────────────────────────────────────────────────

const CreateSingleTrackFormContent: React.FC<{
    projects: any[];
    projectsData: { [key: string]: any };
    setProjectsData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
    loadingProjects: boolean;
    isDataLoading: boolean;
    LANGUAGES: { code: string; name: string }[];
    activeLanguageTab: number;
    handleLanguageTabChange: (idx: number) => void;
}> = ({
    projects,
    projectsData,
    setProjectsData,
    loadingProjects,
    isDataLoading,
    LANGUAGES,
    activeLanguageTab,
    handleLanguageTabChange,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue, errors, touched } = useFormikContext<UnifiedFormValues>();
    const imageValidation = useImageValidation({
        type: "hubStory",
        imageUrl: values.pictures[0] || undefined,
    });

    const fetchProjectDetails = async (projectId: string) => {
        if (projectsData[projectId]) return projectsData[projectId];
        try {
            const token = localStorage.getItem("token");
            if (!token) return null;
            const response = await getProject({ token, projectId });
            if (response) {
                setProjectsData((prev) => ({ ...prev, [projectId]: response }));
                return response;
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
        }
        return null;
    };

    const handlePicturesSelected = useCallback(
        async (files: File | File[]) => {
            const fileList = Array.isArray(files) ? files : [files];
            const token = localStorage.getItem("token");
            if (!token) return;
            await Promise.all(fileList.map((file) => imageValidation.validateImage(file)));
            Promise.all(
                fileList.map((file) => uploadFileToS3Complete({ file, isPublic: true, isFolder: false }, token)),
            )
                .then((results) => setFieldValue("pictures", [...values.pictures, ...results]))
                .catch(console.error);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, values.pictures, imageValidation.validateImage],
    );

    const setPicturesFromList = useCallback(
        (pictures: string[]) => setFieldValue("pictures", pictures),
        [setFieldValue],
    );

    const handleAudioFileSelected = useCallback(
        (langCode: string) => (files: File | File[]) => {
            const file = Array.isArray(files) ? files[0] : files;
            if (!file) return;
            const token = localStorage.getItem("token");
            if (!token) return;
            uploadFileToS3Complete({ file, isPublic: true, isFolder: false }, token)
                .then((result) => setFieldValue(`localizedContent.${langCode}.audioUrl`, result))
                .catch(console.error);
        },
        [setFieldValue],
    );

    const handleProjectSelection = useCallback(
        (langCode: string, projectId: string) => {
            setFieldValue(`localizedContent.${langCode}.selectedProject`, projectId);
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
            if (projectId) fetchProjectDetails(projectId);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue],
    );

    const handleAudioSelection = useCallback(
        (langCode: string, audioUrl: string) => {
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, audioUrl);
            setFieldValue(`localizedContent.${langCode}.audioUrl`, audioUrl);
        },
        [setFieldValue],
    );

    const handleProjectToggle = useCallback(
        (langCode: string, useProject: boolean) => {
            setFieldValue(`localizedContent.${langCode}.useProject`, useProject);
            if (!useProject) {
                setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
                setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            }
        },
        [setFieldValue],
    );

    const handleRemoveAudio = useCallback(
        (langCode: string) => {
            setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
            setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            setFieldValue(`localizedContent.${langCode}.useProject`, false);
        },
        [setFieldValue],
    );

    const getAudioOptionsForLanguage = (langCode: string, projectId: string) => {
        const project = projectsData[projectId];
        if (!project) return [];
        return (
            project.audioAI
                ?.filter((audio: any) => audio.language === langCode)
                ?.sort((a: any, b: any) => a.position - b.position)
                .map((audio: any) => ({ value: audio.audioUrl, label: audio.name })) || []
        );
    };

    if (isDataLoading) {
        return (
            <div className="form-loading-container">
                <div className="form-loading-spinner" />
                <p className="form-loading-text">{t("loadingFormData")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div>
                {/* Position */}
                <FormGroup
                    title={t("position")}
                    subtitle={t("positionDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <FastField name="position" component={NumberField} placeholder={t("enterPosition")} min={0} />
                </FormGroup>

                {/* Picture */}
                <FormGroup title={t("pictures")} className={`${stepStyles.stepDescription} form-group-spacing`}>
                    <ImageSpecsBadges spec="hubStory" warnings={imageValidation.warnings} />
                    <div className={`${stepStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handlePicturesSelected}
                            showButton={true}
                            buttonVariant="outline"
                            multiple={false}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_200_KB}
                            maxSizeLabel={t("imageMaxSize200kb")}
                        />
                    </div>
                    {values.pictures.length > 0 && (
                        <div className="photo-gallery form-photo-gallery-constrained">
                            <ImageList pictures={values.pictures} setPictures={setPicturesFromList} />
                        </div>
                    )}
                </FormGroup>

                {/* Information block */}
                <FormGroup
                    title={t("contactInformation")}
                    subtitle={t("downloadLinkDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={infoStyles.fields}>
                        {/* Address */}
                        <div>
                            <label className={infoStyles.label}>{t("address")}</label>
                            <FastField name="address" placeholder={t("enterAddress")} component={TextField} />
                        </div>

                        {/* Coordinates – only shown when address is filled */}
                        {values.address && values.address.trim() !== "" && (
                            <div>
                                <label className={infoStyles.label}>{t("coordinates")}</label>
                                <Field name="coordinates" placeholder={t("enterCoordinates")} component={TextField} />
                                {touched.coordinates && errors.coordinates && (
                                    <span className="error-message">{errors.coordinates}</span>
                                )}
                            </div>
                        )}

                        {/* Phone */}
                        <div>
                            <label className={infoStyles.label}>{t("phone")}</label>
                            <FastField name="phone" placeholder={t("phoneNumber")} component={TextField} />
                        </div>

                        {/* Website / Download link – mutually exclusive */}
                        <div className={infoStyles.exclusiveBlock}>
                            <div
                                className={
                                    values.downloadLink && values.downloadLink.trim() !== ""
                                        ? infoStyles.fieldGroupDisabled
                                        : undefined
                                }
                            >
                                <label className={infoStyles.label}>{t("websiteLinkLabel")}</label>
                                <Field
                                    name="linkUrl"
                                    placeholder={t("websiteLinkPlaceholder")}
                                    component={TextField}
                                    disabled={!!(values.downloadLink && values.downloadLink.trim() !== "")}
                                />
                            </div>

                            <div
                                className={
                                    values.linkUrl && values.linkUrl.trim() !== ""
                                        ? infoStyles.fieldGroupDisabled
                                        : undefined
                                }
                            >
                                <label className={infoStyles.label}>{t("downloadLinkLabel")}</label>
                                <Field
                                    name="downloadLink"
                                    placeholder={t("downloadLinkPlaceholder")}
                                    component={TextField}
                                    disabled={!!(values.linkUrl && values.linkUrl.trim() !== "")}
                                />
                                {touched.downloadLink && errors.downloadLink && (
                                    <span className="error-message">{errors.downloadLink}</span>
                                )}
                            </div>
                            <p className={infoStyles.exclusiveHint}>{t("mutuallyExclusiveHint")}</p>
                        </div>
                    </div>
                </FormGroup>

                {/* Localized content */}
                <FormGroup
                    title={t("localizedVersions")}
                    tooltip={t("localizedVersionsTooltip")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                    style={{ marginTop: "32px" }}
                >
                    <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2, mb: 2 }}>
                        <Tabs
                            value={activeLanguageTab}
                            onChange={(_, newValue) => handleLanguageTabChange(newValue)}
                            sx={{
                                position: "sticky",
                                top: 0,
                                backgroundColor: "white",
                                zIndex: 10,
                                borderBottom: "1px solid #e0e0e0",
                                "& .MuiTabs-flexContainer": { minHeight: "48px" },
                                "& .MuiTab-root": { minHeight: "48px", textTransform: "none", fontWeight: 500 },
                            }}
                        >
                            {LANGUAGES.map((lang) => (
                                <Tab key={lang.code} label={lang.name} />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ pt: 2, pb: 4 }}>
                        {LANGUAGES.map((lang, index) => {
                            const langCode = lang.code;
                            const currentContent = values.localizedContent[langCode] || {
                                name: "",
                                description: "",
                                audioUrl: "",
                                useProject: false,
                                selectedProject: "",
                                selectedAudioOption: "",
                            };
                            const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));
                            const audioOptions = currentContent.selectedProject
                                ? getAudioOptionsForLanguage(langCode, currentContent.selectedProject)
                                : [];

                            return (
                                <Box
                                    key={lang.code}
                                    sx={{ display: activeLanguageTab === index ? "block" : "none", pt: 0, pb: 2 }}
                                >
                                    <FormGroup
                                        title={`${t("name")} (${lang.name})`}
                                        tooltip={t("nameFieldTooltip")}
                                        className={stepStyles.stepDescription}
                                        style={{ marginTop: 0, marginBottom: 20 }}
                                    >
                                        <FastField
                                            name={`localizedContent.${langCode}.name`}
                                            placeholder={t("enterName")}
                                            component={TextField}
                                        />
                                    </FormGroup>

                                    <FormGroup
                                        title={`${t("audio")} (${lang.name})`}
                                        className={stepStyles.stepDescription}
                                        style={{ marginTop: 0, marginBottom: 20 }}
                                    >
                                        <div className="editor-section">
                                            <FileInput
                                                onFileSelected={handleAudioFileSelected(langCode)}
                                                placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                                                acceptedFileTypes={audioAccept}
                                            />

                                            <div className="radio-text-bold" style={{ marginTop: "20px" }}>
                                                <RadioButton
                                                    label={t("addFromYourProjects")}
                                                    checked={currentContent.useProject}
                                                    onChange={() =>
                                                        handleProjectToggle(langCode, !currentContent.useProject)
                                                    }
                                                    activeImage={activeRadio}
                                                    inactiveImage={inactiveRadio}
                                                    center={true}
                                                />
                                            </div>

                                            {currentContent.useProject && (
                                                <div
                                                    className="dropdown-row"
                                                    style={{ marginTop: "10px", marginLeft: 0 }}
                                                >
                                                    <Dropdown
                                                        label=""
                                                        options={projectOptions}
                                                        onChange={(value) => handleProjectSelection(langCode, value)}
                                                        placeholder={t("selectProject")}
                                                        loading={false}
                                                        error={false}
                                                        value={currentContent.selectedProject}
                                                    />
                                                    {currentContent.selectedProject && (
                                                        <Dropdown
                                                            label=""
                                                            options={audioOptions}
                                                            onChange={(value) => handleAudioSelection(langCode, value)}
                                                            loading={false}
                                                            error={false}
                                                            placeholder={t("selectAnAudio")}
                                                            value={currentContent.selectedAudioOption}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {currentContent.audioUrl && (
                                                <div className={audioForm.container}>
                                                    <div className={audioForm.header}>
                                                        <span className={audioForm.title}>
                                                            {t("audioTrack")} ({lang.name})
                                                        </span>
                                                        <Button
                                                            label={t("remove")}
                                                            onClick={() => handleRemoveAudio(langCode)}
                                                            variant="danger"
                                                            isSmall
                                                            preventDefault
                                                            className={audioForm.removeBtn}
                                                        />
                                                    </div>
                                                    <HorizontalSeparator />
                                                    <div className={audioForm.content}>
                                                        <AudioPlayerRewind
                                                            title=""
                                                            src={currentContent.audioUrl}
                                                            isAutoPlay={false}
                                                            hideDownload={true}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </FormGroup>

                                    <FormGroup
                                        title={`${t("description")} (${lang.name})`}
                                        className={`${stepStyles.stepDescription} form-group-spacing`}
                                        style={{ marginTop: 0, marginBottom: 20 }}
                                    >
                                        <FastField
                                            name={`localizedContent.${langCode}.description`}
                                            placeholder={t("enterDescription")}
                                            component={TextField}
                                            size="big-with-border"
                                            numberOfLines={4}
                                            fullWidth
                                        />
                                    </FormGroup>
                                </Box>
                            );
                        })}
                    </Box>
                </FormGroup>
            </div>
        </Form>
    );
};

// ─── Modal shell ──────────────────────────────────────────────────────────────

const UnifiedCreateSingleTrackModal: React.FC<UnifiedCreateSingleTrackModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    hubId,
    folderId,
    categoryId,
    hub,
}) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const { showNotification } = useNotification();
    const modalContentRef = useRef<HTMLDivElement>(null);

    const [projects, setProjects] = useState<any[]>([]);
    const [projectsData, setProjectsData] = useState<{ [key: string]: any }>({});
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [activeLanguageTab, setActiveLanguageTab] = useState(0);

    // Language tab sync
    const handleLanguageTabChange = (idx: number) => setActiveLanguageTab(idx);

    // Fetch projects
    useEffect(() => {
        if (!isOpen) return;
        const fetchProjects = async () => {
            setLoadingProjects(true);
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const response = await getProjects({ token });
                if (response) setProjects(response);
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoadingProjects(false);
            }
        };
        fetchProjects();
    }, [isOpen]);

    // Resolve hub languages
    const LANGUAGES: { code: string; name: string }[] = (() => {
        if (hub?.availableLanguagesTab && hub.availableLanguagesTab.length > 0) {
            return hub.availableLanguagesTab.map((langCode: string) => {
                const lang = data.languagesAI?.find((l: any) => l.code === langCode);
                return { code: langCode, name: lang ? lang.label : langCode };
            });
        }
        return [
            { code: "EN", name: "English" },
            { code: "FR", name: "Français" },
        ];
    })();

    // Validation schema
    const validationSchema = Yup.object()
        .shape({
            type: Yup.string().oneOf(["story", "event"]).required(),
            pictures: Yup.array().of(Yup.string()),
            position: Yup.number(),
            address: Yup.string(),
            coordinates: Yup.string()
                .nullable()
                .test("is-valid-coordinates", t("coordinatesMustBeValid"), function (value) {
                    if (!value || value.trim() === "") return true;
                    const parts = value.split(",").map((v) => v.trim());
                    if (parts.length !== 2) return false;
                    const [lat, lng] = parts.map((v) => parseFloat(v));
                    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                })
                .test("coordinates-required-with-address", t("coordinatesRequiredWithAddress"), function (value) {
                    const { address } = this.parent;
                    if (address && address.trim() !== "" && (!value || value.trim() === "")) {
                        return false;
                    }
                    return true;
                }),
            phone: Yup.string(),
            linkUrl: Yup.string(),
            downloadLink: Yup.string(),
            localizedContent: Yup.lazy(() => {
                const shape: any = {};
                LANGUAGES.forEach((lang) => {
                    shape[lang.code] = Yup.object({
                        name: Yup.string(),
                        description: Yup.string(),
                        audioUrl: Yup.string(),
                        useProject: Yup.boolean(),
                        selectedProject: Yup.string(),
                        selectedAudioOption: Yup.string(),
                    });
                });
                return Yup.object(shape);
            }),
        })
        .test("at-least-one-name", t("atLeastOneNameRequired"), function (values) {
            const hasAtLeastOneName = LANGUAGES.some((lang) => {
                const content = values.localizedContent?.[lang.code];
                return content?.name && content.name.trim() !== "";
            });
            if (!hasAtLeastOneName) {
                return this.createError({
                    path: `localizedContent.${LANGUAGES[0]?.code}.name`,
                    message: t("atLeastOneNameRequired"),
                });
            }
            return true;
        })
        .test("website-or-download-exclusive", t("websiteOrDownloadLinkRequired"), function (values) {
            if (
                values.linkUrl &&
                values.linkUrl.trim() !== "" &&
                values.downloadLink &&
                values.downloadLink.trim() !== ""
            ) {
                return this.createError({ path: "downloadLink", message: t("websiteOrDownloadLinkRequired") });
            }
            return true;
        });

    // Initial values
    const getInitialValues = (): UnifiedFormValues => {
        const localizedContent: { [key: string]: LocalizedContent } = {};
        LANGUAGES.forEach((lang) => {
            localizedContent[lang.code] = {
                name: "",
                description: "",
                audioUrl: "",
                useProject: false,
                selectedProject: "",
                selectedAudioOption: "",
            };
        });
        return {
            localizedContent,
            address: "",
            coordinates: "",
            phone: "",
            linkUrl: "",
            downloadLink: "",
            pictures: [],
            type: "story",
            position: 0,
        };
    };

    // Submit
    const handleSubmit = async (values: UnifiedFormValues, formikHelpers: FormikHelpers<UnifiedFormValues>) => {
        const token = localStorage.getItem("token");
        if (!token || !hubId || !categoryId) {
            console.error("Missing token or IDs");
            return;
        }

        try {
            const payload = {
                localizedData: Object.entries(values.localizedContent).map(([langCode, content]) => ({
                    name: content.name,
                    description: content.description || undefined,
                    language: langCode,
                    audioUrl: content.audioUrl || undefined,
                    address: values.address || undefined,
                })),
                coverImage: values.pictures?.[0],
                pictures: values.pictures.map((url, index) => ({ url, position: index })),
                type: "story",
                folderId: hubId,
                categoryId,
                companySingleTrackFolderId: folderId,
                position: values.position,
                phone: values.phone || undefined,
                linkUrl: values.linkUrl || undefined,
                downloadLink: values.downloadLink || undefined,
                point:
                    values.coordinates && values.coordinates.trim() !== ""
                        ? {
                              latitude: parseFloat(values.coordinates.split(",")[0]),
                              longitude: parseFloat(values.coordinates.split(",")[1]),
                          }
                        : undefined,
            };

            await createSingleTrackStory({ token, data: payload });

            showNotification({ message: t("singleTrackStoryCreatedSuccessfully"), type: "success" });
            formikHelpers.resetForm();
            setActiveLanguageTab(0);
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error("Error creating single track:", err);
            showNotification({ message: t("errorCreatingSingleTrack"), type: "error" });
        }
    };

    const flattenErrors = (obj: any): string[] => {
        const result: string[] = [];
        Object.values(obj).forEach((value) => {
            if (typeof value === "string") result.push(value);
            else if (typeof value === "object" && value !== null) result.push(...flattenErrors(value));
        });
        return result;
    };

    if (!isOpen) return null;

    const modal = (
        <div className={studio.overlay}>
            <div
                className={studio.singleTrackContent}
                ref={modalContentRef}
                style={{
                    maxWidth: "90vw",
                    width: "760px",
                    maxHeight: "90vh",
                    padding: "0",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: "20px 20px 14px 20px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <h2
                        style={{
                            margin: 0,
                            fontSize: "22px",
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                            color: "#1a202c",
                        }}
                    >
                        {t("createSingleTrack")}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: "22px",
                            cursor: "pointer",
                            color: "#666",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Scrollable body */}
                <div style={{ flex: 1, overflow: "auto", padding: "20px 24px 0 24px" }}>
                    <Formik
                        initialValues={getInitialValues()}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        key={`create-${i18n.language}-${isOpen}`}
                        validateOnMount
                        enableReinitialize={true}
                        validateOnChange={true}
                        validateOnBlur={true}
                    >
                        {({ isSubmitting, submitForm, errors }) => (
                            <>
                                <CreateSingleTrackFormContent
                                    projects={projects}
                                    projectsData={projectsData}
                                    setProjectsData={setProjectsData}
                                    loadingProjects={loadingProjects}
                                    isDataLoading={loadingProjects}
                                    LANGUAGES={LANGUAGES}
                                    activeLanguageTab={activeLanguageTab}
                                    handleLanguageTabChange={handleLanguageTabChange}
                                />

                                {/* Sticky footer */}
                                <div
                                    style={{
                                        marginTop: "24px",
                                        padding: "16px 0 20px",
                                        borderTop: "1px solid #eee",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "10px",
                                        position: "sticky",
                                        bottom: 0,
                                        backgroundColor: "white",
                                        zIndex: 10,
                                    }}
                                >
                                    <Button
                                        variant="cancel"
                                        label={t("cancel")}
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                    />
                                    <Tooltip
                                        title={
                                            Object.keys(errors).length > 0
                                                ? t("formIncompleteTooltip", {
                                                      fields: flattenErrors(errors).join(", "),
                                                  })
                                                : ""
                                        }
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                variant="primary"
                                                label={t("createSingleTrack")}
                                                onClick={submitForm}
                                                disabled={isSubmitting || Object.keys(errors).length > 0}
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            </>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default UnifiedCreateSingleTrackModal;
